import React, { useState, useEffect } from "react";
import ReactTooltip from "react-tooltip";
import _ from "lodash";

import Title from "../../../../components/common/Title";
import Text from "../../../../components/common/Text";
import { InputText } from "../../../../components/common/Input";
import { useEvent } from "../../../../Store/Event/Show";
import PlusIcon from "../../../../assets/icons/PlusIcon";
import SwitchWithTitle from "../../../GeneralSettings/components/SwitchWithTitle";
import "./shareTemplate.css";

import {
  storeShareTemplates,
  getShareTemplates,
  toggleShareTemplate,
} from "../../../../apis";
import Information from "../../../../assets/images/information.png";

const ShareTemplate = () => {
  const { event } = useEvent();
  const [templates, setTemplates] = useState([{ id: 1, text: "" }]);
  const [isEnabled, setIsEnabled] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const callShareTemplates = async () => {
    try {
      const { data } = await getShareTemplates(event.id);
      if (data.data.length) {
        const formattedTemplates = data.data.map((template, index) => ({
          id: template.id,
          text: template.share_message,
        }));
        setTemplates(formattedTemplates);
        setIsEnabled(event?.isCustomShareEnabled);
      } else {
        setTemplates([{ id: 1, text: "" }]);
        setIsEnabled(event?.isCustomShareEnabled || false);
      }
    } catch (error) {
      console.log(error);
      console.error(error);
      window.Toast.fire({ icon: "error", title: "Something went wrong" });
    }
  };

  useEffect(() => {
    if (event.id) {
      callShareTemplates();
    }
  }, [event.id]);

  const handleTextChange = (id, value) => {
    const updatedTemplates = templates.map((template) =>
      template.id === id ? { ...template, text: value } : template
    );
    setTemplates(updatedTemplates);
  };

  const increaseTemplate = () => {
    setTemplates((prev) => [...prev, { id: prev.length + 1, text: "" }]);
  };

  const removeTemplate = (index) => {
    if (templates.length > 1) {
      const templatesCopy = [...templates];
      templatesCopy.splice(index, 1);
      setTemplates(templatesCopy);
    }
  };

  const handleSave = () => {
    if (!isEnabled) {
      return;
    }
    const templatesData = templates.map((t) => ({
      user_id: event.user_id,
      event_id: event.id,
      share_message: t.text,
    }));
    storeShareTemplates(templatesData)
      .then(function (response) {
        window.Toast.fire({
          icon: "success",
          title: "Templates saved successfully",
        });
      })
      .catch((error) => {
        window.Toast.fire({
          icon: "error",
          title: "Failed to save templates",
        });
      });
  };

  const handleToggle = async (newValue) => {
    setIsLoading(true);

    try {
      setIsEnabled(newValue);
      const { data } = await toggleShareTemplate(event.id, newValue);
      if (data?.data?.isCustomShareEnabled != null) {
        window.Toast.fire({
          icon: "success",
          title: newValue
            ? "Share templates enabled"
            : "Share templates disabled",
        });

        if (!newValue) {
          setTemplates([{ id: 1, text: "" }]);
        }
      } else {
        throw new Error("Failed to toggle share template");
      }
    } catch (error) {
      window.Toast.fire({
        icon: "error",
        title: "Failed to update share template settings",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Title
        className="pb-4 mb-6 border-b border-secondary-600 mt-4"
        text={
          <>
            <div>Custom Share Message</div>{" "}
            <span
              className="pl-3 cursor-pointer relative"
              data-tip
              data-for="customShareMessage"
            >
              <img className="cursor-pointer" src={Information} />
              <ReactTooltip
                id="customShareMessage"
                multiline={true}
                type="dark"
                className="z-[100] !opacity-100"
              >
                <div>
                  <h3 className="text-xl font-medium mb-1">Feature Overview</h3>
                  <ul className="list-disc px-4 mb-2 text-sm">
                    <li>
                      Create custom share message so message will also be shared
                      whenever the image is shared by the client giving more
                      recognition
                    </li>
                  </ul>
                  <div className="w-full mt-2 mb-2 h-[1px] bg-[#555555]"></div>
                  <h3 className="text-xl font-medium mb-1">How to use</h3>
                  <ol className="list-decimal px-4 mb-2 text-sm">
                    <li>
                      Enable the custom share button
                      <li>Add your message</li>
                      <li>Click on + to add more messaged</li>
                      <li>Click on save to save all the messages</li>
                    </li>
                  </ol>
                  <video
                    muted
                    autoPlay
                    loop
                    playsinline
                    src={
                      "https://img2.pixnif.com/assets/videos/shareHelper.mov"
                    }
                    height={400}
                    width={400}
                  />
                </div>
              </ReactTooltip>
            </span>
          </>
        }
      />

      <div className="flex flex-wrap 2xl:flex-nowrap gap-x-32 gap-y-8 mt-8">
        <div className="flex flex-col basis-1/2">
          <SwitchWithTitle
            className="mb-8 font-medium"
            title="Enable Custom Share Message"
            subtitle="Turn on to configure custom share messages for social media"
            value={isEnabled}
            onChange={handleToggle}
            disabled={isLoading}
          />
        </div>

        <div className="flex flex-col basis-1/2">
          {isEnabled ? (
            <>
              <div className="flex flex-col gap-y-6">
                {templates.map((template, index) => (
                  <div
                    key={template.id}
                    className="flex justify-center items-center gap-x-2"
                  >
                    <div className="w-full">
                      <div className="flex justify-between items-center pr-5 rounded-[10px] bg-dark-gray mt-1 text-light-gray shadow-input-empty">
                        <input
                          className="pl-4 bg-transparent py-2 placeholder:text-secondary-500 placeholder:text-sm outline-none w-full text-light-gray text-sm"
                          value={template.text}
                          onChange={(e) =>
                            handleTextChange(template.id, e.target.value)
                          }
                          placeholder="Enter template text"
                        />
                      </div>
                    </div>

                    <button
                      onClick={
                        index === 0
                          ? increaseTemplate
                          : () => removeTemplate(index)
                      }
                      className="shadow-card h-8 w-8 px-2 mt-1"
                    >
                      {index === 0 ? (
                        <PlusIcon width={16} height={16} />
                      ) : (
                        <span className="text-2xl">-</span>
                      )}
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end mt-8">
                <button
                  onClick={handleSave}
                  className="text-sm text-center text-light-gray shadow-card rounded-[10px] py-[9px] px-[16px] font-semibold w-[8rem] h-[2.5rem]"
                >
                  Save
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ShareTemplate;
